import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { DownloadType } from './dto/enum/download-type.enum';
import { AnalyticsService } from '../../service/analytics.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { IUrlProvider } from './service/url.provider';
import { FORM_PROVIDERS, URL_PROVIDERS } from './token';
import { PhotoUrlProvider } from './service/impl/url/photo-url.provider';
import { VideoUrlProvider } from './service/impl/url/video-url.provider';
import { ReelUrlProvider } from './service/impl/url/reel-url.provider';
import { EMPTY } from '../../util/const';
import { PhotoFormProvider } from './service/impl/form/photo-form.provider';
import { VideoFormProvider } from './service/impl/form/video-form.provider';
import { ReelFormProvider } from './service/impl/form/reel-form.provider';
import { IFormProvider } from './service/form.provider';
import { Subscription, timer } from 'rxjs';
import { IUrlProviderDTO } from './dto/url-provider.dto';
import { SentryService } from '../../service/sentry.service';
import { DownloadState } from './dto/enum/download-state.enum';
import { DownloadService } from '../../service/download.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { AvatarUrlProvider } from './service/impl/url/avatar-url.provider';
import { AvatarFormProvider } from './service/impl/form/avatar-form.provider';
import { CloudflareTurnstileService } from '../../service/cloudflare-turnstile.service';
import { unsubscribe } from '../../util/rx.util';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-download-form',
  imports: [
    NzFormModule,
    ReactiveFormsModule,
    NzInputGroupComponent,
    NzInputDirective,
    NzButtonComponent,
    NzIconDirective,
  ],
  providers: [
    { provide: URL_PROVIDERS, useClass: PhotoUrlProvider, multi: true },
    { provide: URL_PROVIDERS, useClass: VideoUrlProvider, multi: true },
    { provide: URL_PROVIDERS, useClass: ReelUrlProvider, multi: true },
    { provide: URL_PROVIDERS, useClass: AvatarUrlProvider, multi: true },
    { provide: FORM_PROVIDERS, useClass: PhotoFormProvider, multi: true },
    { provide: FORM_PROVIDERS, useClass: VideoFormProvider, multi: true },
    { provide: FORM_PROVIDERS, useClass: ReelFormProvider, multi: true },
    { provide: FORM_PROVIDERS, useClass: AvatarFormProvider, multi: true },
  ],
  templateUrl: './download-form.component.html',
  styleUrl: './download-form.component.less',
})
export class DownloadFormComponent implements OnInit, OnDestroy {
  private static readonly SERVER_RESPONSES: Map<number, DownloadState> =
    new Map<number, DownloadState>([
      [400, DownloadState.VALIDATION_ERROR],
      [403, DownloadState.CAPTCHA_ERROR],
      [404, DownloadState.NOT_FOUND_ERROR],
    ]);
  public readonly DownloadState: typeof DownloadState = DownloadState;
  public readonly type: InputSignal<DownloadType> =
    input.required<DownloadType>();
  public readonly stateEventEmitter: OutputEmitterRef<DownloadState> =
    output<DownloadState>();
  public formGroup!: FormGroup;
  public readonly state: WritableSignal<DownloadState> = signal<DownloadState>(
    DownloadState.INIT
  );
  public formProvider!: IFormProvider;
  private readonly router: Router = inject(Router);
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly downloadService: DownloadService = inject(DownloadService);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly cloudflareTurnstileService: CloudflareTurnstileService =
    inject(CloudflareTurnstileService);
  private readonly urlProviders: IUrlProvider[] = inject(URL_PROVIDERS);
  private readonly formProviders: IFormProvider[] = inject(FORM_PROVIDERS);
  private urlProvider!: IUrlProvider;
  private urlSub$?: Subscription = undefined;
  private captchaSub$?: Subscription = undefined;

  public ngOnInit(): void {
    const type: DownloadType = this.type();
    const urlProvider: IUrlProvider | undefined = this.urlProviders.find(
      (urlProvider: IUrlProvider): boolean => type === urlProvider.getType()
    );
    if (!urlProvider) {
      throw new Error(`UrlProvider with type [${type}] not found!`);
    }
    this.urlProvider = urlProvider;
    const formProvider: IFormProvider | undefined = this.formProviders.find(
      (formProvider: IFormProvider): boolean => type === formProvider.getType()
    );
    if (!formProvider) {
      throw new Error(`FormProvider with type [${type}] not found!`);
    }
    this.formProvider = formProvider;
    this.formGroup = new FormGroup({
      payload: new FormControl(
        { value: EMPTY, disabled: false },
        this.formProvider.getValidators()
      ),
    });
  }

  public ngOnDestroy(): void {
    unsubscribe(this.urlSub$);
    unsubscribe(this.captchaSub$);
  }

  public onPayloadInput(event: Event): void {
    this.formGroup.patchValue({
      payload: this.formProvider.getOnInput()(event),
    });
    this.nextState(
      this.formGroup.valid ? DownloadState.INIT : DownloadState.VALIDATION_ERROR
    );
  }

  public submitForm(): void {
    if (!this.formGroup || this.formGroup.invalid) {
      return;
    }
    const payload: string =
      this.formGroup.get('payload')!.value?.trim() ?? EMPTY;
    if (!payload) {
      return;
    }
    this.analyticsService.event('download_exec', {
      origin: 'form',
      payload: payload,
      path: this.router.url,
    });
    this.formGroup.disable();
    this.nextState(DownloadState.LOADING);
    this.captchaSub$ = this.cloudflareTurnstileService
      .execute(
        this.urlProvider.getCaptchaAction(),
        this.urlProvider.getCaptchaCData(payload)
      )
      .subscribe({
        next: (token: string): void => this.download(payload, token),
        error: (error: unknown): void => {
          this.nextState(DownloadState.CAPTCHA_ERROR);
          this.formGroup.enable();
          console.error(
            'An error occurred during fetching captcha token',
            error
          );
        },
      });
  }

  private download(payload: string, captchaToken: string): void {
    this.urlSub$ = this.urlProvider.getUrls(payload, captchaToken).subscribe({
      next: (urlProviderDTO: IUrlProviderDTO): void => {
        if (urlProviderDTO.urls.length) {
          this.nextState(DownloadState.OK);
          timer(1250).subscribe((): void => {
            urlProviderDTO.urls
              .map((url: string): string =>
                this.downloadService.getDownloadUrl(url)
              )
              .forEach((url: string): Window | null =>
                window.open(url, '_blank')
              );
            window.open('https://otieu.com/4/9441045', '_blank')?.focus();
          });
        } else {
          this.nextState(DownloadState.NOT_FOUND_ERROR);
        }
        this.formGroup.enable();
      },
      error: (error: HttpErrorResponse): void => {
        this.nextState(
          DownloadFormComponent.SERVER_RESPONSES.get(error.status) ??
            DownloadState.UNEXPECTED_ERROR
        );
        if (400 === error.status) {
          // https://github.com/angular/angular/issues/48515
          // https://github.com/ngx-formly/ngx-formly/issues/2290
          timer(0).subscribe((): void => {
            this.formGroup
              .get('payload')!
              .setErrors({ serverConstraintViolation: true });
          });
        }
        this.formGroup.enable();
        console.error(
          `An error occurred during downloading by payload [${payload}]`,
          error
        );
        if (![400, 403, 404].includes(error.status)) {
          this.sentryService.captureException({
            error,
            severityLevel: 'error',
            transactionName: 'DOWNLOAD_ERROR',
          });
        }
      },
    });
  }

  private nextState(nextState: DownloadState): void {
    if (this.state() !== nextState) {
      this.state.set(nextState);
      this.stateEventEmitter.emit(nextState);
    }
  }
}
